import { CalendarUnit, DataType, FilterOperator, IndexType } from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import {
  UNITS,
  canExcludeCurrent,
  describeExcludeCurrent,
  formatDate,
  formatDateTimeByLocale,
  isValidDate,
  isValidDateTimeString,
  isValidMuiDateTimeString,
  toDateTimeObject,
} from '../dates.js'
import { DossError } from '../doss-error.js'
import { isDuplicateFilterAllowedColumnType } from '../duplicate-filter.js'
import { LocaleCode } from '../locales.js'
import { formatNumberByLocale } from '../numbers.js'
import { titleCase } from '../string.js'
import { isDateColumn, isNumericColumn } from '../table-column.js'

const emptyOperandSchema = yup
  .object({
    data: yup.mixed().nullable().oneOf([null]).defined(),
  })
  .strict()
const stringOperandSchema = yup
  .object({
    data: yup.string().required(),
  })
  .strict()
const numberOperandSchema = yup
  .object({
    data: yup.number().required(),
  })
  .strict()
const booleanOperandSchema = yup
  .object({
    data: yup.boolean().required(),
  })
  .strict()
export const dateTimeOperandSchema = yup
  .object({
    data: yup.string().required(),
    offset: yup.number().notRequired(),
    // offsetUnit is required if offset is provided
    offsetUnit: yup
      .string()
      .oneOf(Object.values(CalendarUnit))
      .when('offset', ([offset], schema) => (offset ? schema.required() : schema.optional())),
    floorUnit: yup.string().oneOf(Object.values(CalendarUnit)).optional(),
  })
  .test('validate-data-format', 'Invalid date format', (value) => {
    if (_.isNil(value)) {
      /* Yup runs the test even if this schema is not required at the parent level,
       * so we have to manually allow the test to pass when the value is nullish
       */
      return true
    }
    const data = value.data
    return (
      data === 'now' ||
      isValidDate(data) ||
      isValidDateTimeString(data) ||
      isValidMuiDateTimeString(data)
    )
  })
  .strict()
const stringArrayOperandSchema = yup
  .object({
    data: yup.array().of(yup.string().required()).required(),
  })
  .strict()
const numberArrayOperandSchema = yup
  .object({
    data: yup.array().of(yup.number().required()).required(),
  })
  .strict()
const booleanArrayOperandSchema = yup
  .object({
    data: yup.array().of(yup.boolean().required()).required(),
  })
  .strict()
const dateArrayOperandSchema = yup
  .object({
    data: yup.array().of(yup.string().datetime().required()).required(),
  })
  .strict()
export const staticDateTimeRangeOperandSchema = yup
  .object({
    data: yup
      .object({
        lower: dateTimeOperandSchema.required(),
        upper: dateTimeOperandSchema.required(),
      })
      .required(),
  })
  .strict()
export type StaticDateTimeRangeOperandData = yup.InferType<typeof staticDateTimeRangeOperandSchema>
/** Static range shape that allows empty `data` strings — for in-progress UI. */
export const partialStaticDateTimeRangeOperandSchema = yup
  .object({
    data: yup
      .object({
        lower: yup.object({ data: yup.string().defined() }).required(),
        upper: yup.object({ data: yup.string().defined() }).required(),
      })
      .required(),
  })
  .strict()
export const dynamicDateTimeRangeOperandSchema = yup
  .object({
    data: yup
      .object({
        dynamicValue: yup
          .object({
            type: yup.string().oneOf(['relative', 'this']).required(),
            unit: yup
              .string()
              .oneOf([...UNITS.THIS, ...UNITS.TIME])
              .required(),
            operator: yup.string().optional(),
            value: yup.number().optional(),
            excludeCurrent: yup.string().oneOf(UNITS.EXCLUDE_CURRENT).optional(),
          })
          .required(),
      })
      .required(),
  })
  .strict()
export type DynamicDateTimeRangeOperandData = yup.InferType<
  typeof dynamicDateTimeRangeOperandSchema
>
export const thisPeriodDynamicValueSchema = yup.object({
  type: yup.string().oneOf(['this']).required(),
  unit: yup.string().oneOf(UNITS.THIS).required(),
  // "This month" already is the current period, so reject an exclusion instead of ignoring it
  excludeCurrent: yup
    .mixed()
    .test(
      'no-exclude-current-on-this',
      'Excluding the current period is not supported for "this" ranges',
      (excludeCurrent) => excludeCurrent === undefined,
    ),
})
export const relativePeriodDynamicValueSchema = yup.object({
  type: yup.string().oneOf(['relative']).required(),
  unit: yup.string().oneOf(UNITS.TIME).required(),
  // Tried to use UNITS.OPERATOR and filter out "this" but build always throw an error thinking "this" is still part of it
  operator: yup.string().oneOf(['last', 'notInTheLast', 'inTheNext']).required(),
  value: yup.number().required(),
  // Evaluators only implement this for `last` and `inTheNext` with day+ units,
  // so reject it elsewhere instead of ignoring it
  excludeCurrent: yup
    .string()
    .oneOf(UNITS.EXCLUDE_CURRENT)
    .optional()
    .test(
      'exclude-current-only-for-day-ranges',
      'Excluding the current period is only supported for "last" and "in the next" with days, weeks, months or years',
      (excludeCurrent, context) => {
        const { operator, unit } = context.parent as { operator?: string; unit?: string }
        return excludeCurrent === undefined || canExcludeCurrent(operator ?? '', unit ?? '')
      },
    ),
})
export const dateTimeRangeOperandSchema = yup
  .object({
    data: yup
      .object({
        lower: dateTimeOperandSchema.optional(),
        upper: dateTimeOperandSchema.optional(),
        dynamicValue: yup
          .object({
            type: yup.string().oneOf(['relative', 'this']).required(),
            unit: yup
              .string()
              .oneOf([...UNITS.THIS, ...UNITS.TIME])
              .required(),
            operator: yup.string().optional(),
            value: yup.number().optional(),
            excludeCurrent: yup.string().oneOf(UNITS.EXCLUDE_CURRENT).optional(),
          })
          .optional(),
      })
      .test(
        'validate-range-data',
        'One of static or dynamic range needs to be provided',
        (value) => {
          return (!_.isNil(value.lower) && !_.isNil(value.upper)) || !_.isNil(value.dynamicValue)
        },
      )
      .required(),
  })
  .strict()
const numberRangeOperandSchema = yup
  .object({
    data: yup
      .object({
        lower: numberOperandSchema.required(),
        upper: numberOperandSchema.required(),
      })
      .required(),
  })
  .strict()

export type EmptyOperandData = yup.InferType<typeof emptyOperandSchema>
export type StringOperandData = yup.InferType<typeof stringOperandSchema>
export type NumberOperandData = yup.InferType<typeof numberOperandSchema>
export type BooleanOperandData = yup.InferType<typeof booleanOperandSchema>
export type DateTimeOperandData = yup.InferType<typeof dateTimeOperandSchema>
export type StringArrayOperandData = yup.InferType<typeof stringArrayOperandSchema>
export type NumberArrayOperandData = yup.InferType<typeof numberArrayOperandSchema>
export type BooleanArrayOperandData = yup.InferType<typeof booleanArrayOperandSchema>
export type DateArrayOperandDate = yup.InferType<typeof dateArrayOperandSchema>
export type DateTimeRangeOperandData = yup.InferType<typeof dateTimeRangeOperandSchema>
export type NumberRangeOperandData = yup.InferType<typeof numberRangeOperandSchema>
export type StaticRangeOperandData = StaticDateTimeRangeOperandData | NumberRangeOperandData
export type ArrayOperandData =
  | StringArrayOperandData
  | NumberArrayOperandData
  | BooleanArrayOperandData
  | DateArrayOperandDate
export type OperandData =
  | EmptyOperandData
  | StringOperandData
  | NumberOperandData
  | BooleanOperandData
  | DateTimeOperandData
  | ArrayOperandData
  | DateTimeRangeOperandData
  | NumberRangeOperandData

/**
 * Check that the column data type, filter operator, and operand are valid
 */
export function checkValidColumnFilterOperand(
  columnType: DataType,
  lookupType: DataType | null,
  indexType: IndexType | null,
  operator: FilterOperator,
  operand: unknown,
): operand is OperandData {
  if (operator === FilterOperator.IS_DUPLICATE && columnType === DataType.LOOKUP) {
    return emptyOperandSchema.isValidSync(operand)
  }
  let columnDataType = lookupType ?? columnType
  if (columnDataType === DataType.ID) {
    if (indexType === null || indexType === IndexType.NONE) {
      throw new DossError('ID column must have an index type', [])
    }
    columnDataType = indexType === IndexType.NUMBER ? DataType.NUMBER : DataType.STRING
  }
  switch (operator) {
    case FilterOperator.IS_DUPLICATE:
      return (
        isDuplicateFilterAllowedColumnType(columnType) && emptyOperandSchema.isValidSync(operand)
      )
    case FilterOperator.IS_EMPTY:
    case FilterOperator.IS_NOT_EMPTY:
      return emptyOperandSchema.isValidSync(operand)
    case FilterOperator.IS_ANY_OF:
    case FilterOperator.IS_NONE_OF:
      return checkArrayComparisonFilter(columnDataType, operand)
    case FilterOperator.GREATER_THAN:
    case FilterOperator.GREATER_THAN_OR_EQUAL_TO:
    case FilterOperator.LESS_THAN:
    case FilterOperator.LESS_THAN_OR_EQUAL_TO:
      return checkNumericComparisonFilter(columnDataType, operand)
    case FilterOperator.EQUALS:
    case FilterOperator.IS_NOT_EQUAL_TO:
      return checkEqualityComparisonFilter(columnDataType, operand)
    case FilterOperator.ENDS_WITH:
    case FilterOperator.STARTS_WITH:
      return checkStringComparisonFilter(columnDataType, operand)
    case FilterOperator.CONTAINS:
    case FilterOperator.DOES_NOT_CONTAIN:
      return checkContainFilter(columnType, lookupType, operand)
    case FilterOperator.IN:
    case FilterOperator.NOT_IN:
      return checkStringComparisonFilter(columnDataType, operand)
    case FilterOperator.RANGE:
    case FilterOperator.NOT_IN_RANGE:
      return checkRangeFilter(columnDataType, operand)
  }
}

/**
 * Given operand data for date or date-time,
 * check if it has both lower and upper bounds and that the lower bound is less than the upper bound
 * then return if the range is a valid static date range
 */
export function validateStaticDateRangeFilter(
  data: unknown,
): data is StaticDateTimeRangeOperandData {
  if (staticDateTimeRangeOperandSchema.isValidSync(data)) {
    const { lower, upper } = data.data
    // Check for empty strings
    if (!lower.data || !upper.data) {
      return false
    }

    return (
      toDateTimeObject(data.data.lower.data).isBefore(toDateTimeObject(data.data.upper.data)) ||
      toDateTimeObject(data.data.lower.data).isSame(toDateTimeObject(data.data.upper.data))
    )
  }
  return false
}

/**
 * Given operand data for date or date-time,
 * check if it has the dynamic value and that the dynamic value is valid for the given type
 * then return if the range is a valid dynamic date range
 */
export function validateDynamicDateRangeFilter(
  data: unknown,
): data is DynamicDateTimeRangeOperandData {
  if (dynamicDateTimeRangeOperandSchema.isValidSync(data)) {
    if (
      data.data.dynamicValue.type === 'this' &&
      thisPeriodDynamicValueSchema.isValidSync(data.data.dynamicValue)
    ) {
      return true
    } else if (
      data.data.dynamicValue.type === 'relative' &&
      relativePeriodDynamicValueSchema.isValidSync(data.data.dynamicValue)
    ) {
      return true
    }
  }
  return false
}

/**
 * Given unvalidated operand data and the column data type,
 * check if the operand data is valid for a static range filter
 */
export function validateStaticRangeFilter(
  columnDataType: DataType,
  data: Record<string, unknown>,
): data is StaticRangeOperandData {
  if (isNumericColumn(columnDataType)) {
    return numberRangeOperandSchema.isValidSync(data)
  } else if (isDateColumn(columnDataType)) {
    return validateStaticDateRangeFilter(data)
  }
  return false
}

/*
 * Validates the operator and operand relationship for FilterOperators `RANGE` and `NOT_IN_RANGE`
 * If filter operand should be a range, the column data type must be numeric or date-like (per
 * `isNumericColumn`/`isDateColumn`, which also cover the FORMULA_NUMBER/FORMULA_DATE/
 * FORMULA_DATE_TIME formula-output subtypes).
 * If the column data type is numeric, the data must have lower and upper bounds, where lower < upper.
 * If the column data type is date-like, the data must have lower and upper bounds OR a dynamic value.
 */
export function checkRangeFilter(
  columnDataType: DataType,
  data: unknown,
): data is DateTimeRangeOperandData | NumberRangeOperandData {
  if (isNumericColumn(columnDataType) && numberRangeOperandSchema.isValidSync(data)) {
    return data.data.lower.data < data.data.upper.data
  } else if (isDateColumn(columnDataType)) {
    // The range operand either needs to be a valid static date range or a valid dynamic date range
    const isStaticDate = validateStaticDateRangeFilter(data)
    const isDynamicDate = validateDynamicDateRangeFilter(data)
    return isStaticDate || isDynamicDate
  }
  return false
}

/**
 * If filter operand IS_GREATER_THAN, IS_GREATER_THAN_OR_EQUAL_TO,
 * IS_LESS_THAN, IS_LESS_THAN_OR_EQUAL_TO, operand has to be number if data type is NUMBER or
 * operand has to be a string if data type is DATE or DATE_TIME
 */
function checkNumericComparisonFilter(columnDataType: DataType, data: unknown): boolean {
  if (columnDataType === DataType.NUMBER || columnDataType === DataType.FORMULA_NUMBER) {
    return numberOperandSchema.isValidSync(data)
  } else if (
    columnDataType === DataType.DATE ||
    columnDataType === DataType.DATE_TIME ||
    columnDataType === DataType.FORMULA_DATE ||
    columnDataType === DataType.FORMULA_DATE_TIME
  ) {
    return dateTimeOperandSchema.isValidSync(data)
  }
  return false
}

/**
 * IF filter operand is EQUALS or IS_NOT_EQUAL_TO,
 * has to be number if DataType.NUMBER,
 * has to be boolean if DataType.BOOLEAN, else has to be string
 */
function checkEqualityComparisonFilter(columnDataType: DataType, data: unknown): boolean {
  switch (columnDataType) {
    case DataType.NUMBER:
    case DataType.FORMULA_NUMBER:
      return numberOperandSchema.isValidSync(data)
    case DataType.BOOLEAN:
      return booleanOperandSchema.isValidSync(data)
    case DataType.STRING:
    case DataType.FORMULA_STRING:
    case DataType.FILE_URL:
    case DataType.USER:
    case DataType.ID:
    case DataType.LOOKUP:
      return stringOperandSchema.isValidSync(data)
    case DataType.ENUM:
      // Multi-select enum filters may use EQUALS / IS_NOT_EQUAL_TO with the same string-array
      // operand shape as IS_ANY_OF / IS_NONE_OF (e.g. { data: ['a', 'b'] }).
      return stringOperandSchema.isValidSync(data) || stringArrayOperandSchema.isValidSync(data)
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return dateTimeOperandSchema.isValidSync(data)
    case DataType.ARRAY:
      return false
    case DataType.FORMULA_BOOLEAN:
      return false
  }
}

/**
 * IF filter operand is ENDS_WITH or STARTS_WITH operand must be type string and
 * DataType cannot be boolean or number
 */
function checkStringComparisonFilter(columnDataType: DataType, data: unknown): boolean {
  return (
    columnDataType !== DataType.BOOLEAN &&
    columnDataType !== DataType.NUMBER &&
    stringOperandSchema.isValidSync(data)
  )
}

/**
 * Saved filters / JSON sometimes store numeric isAnyOf operands as string[], e.g. `["14156"]`.
 * Coerce finite numeric strings to numbers; if any element cannot be coerced, return the input unchanged.
 */
export function normalizeNumberArrayIsAnyOfOperand(operand: unknown): unknown {
  if (typeof operand !== 'object' || operand === null || !('data' in operand)) {
    return operand
  }
  const raw = operand.data
  if (!Array.isArray(raw)) {
    return operand
  }
  const next: number[] = []
  for (const item of raw) {
    if (typeof item === 'number') {
      if (!Number.isFinite(item)) {
        return operand
      }
      next.push(item)
    } else if (typeof item === 'string') {
      const trimmed = item.trim()
      if (trimmed === '') {
        return operand
      }
      const n = Number(trimmed)
      if (!Number.isFinite(n)) {
        return operand
      }
      next.push(n)
    } else {
      return operand
    }
  }
  return { ...(operand as Record<string, unknown>), data: next }
}

/**
 * If filter operand is IS_ANY_OF, operand has to be type array of StringOperandData
 * if column is of type DataType.STRING, DataType.ENUM, DataType.FILE_URL
 * or type array of NumberOperandData if column is of type DataType.NUMBER
 *
 * Not a valid filter for array, boolean, date, date-time, formula
 */
function checkArrayComparisonFilter(columnDataType: DataType, data: unknown): boolean {
  switch (columnDataType) {
    case DataType.NUMBER:
    case DataType.FORMULA_NUMBER:
      // Backward compatible: JSON/DB often has numeric isAnyOf as string[]; normalize then validate.
      return numberArrayOperandSchema.isValidSync(normalizeNumberArrayIsAnyOfOperand(data))
    case DataType.STRING:
    case DataType.FILE_URL:
    case DataType.USER:
    case DataType.ID:
    case DataType.LOOKUP:
    case DataType.ENUM:
    case DataType.FORMULA_STRING:
      return stringArrayOperandSchema.isValidSync(data)
    case DataType.ARRAY:
    case DataType.BOOLEAN:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return false
  }
}

/**
 * If filter operand is CONTAINS or DOES_NOT_CONTAIN, data must be a string if column type is STRING
 * or must be an array of strings if column type is ARRAY.
 *
 * For ARRAY<STRING>, ARRAY<FILE_URL>, ARRAY<USER>, etc., `validateFilterArg` uses the inner
 * `resolvedSourceType` (STRING / FILE_URL / USER), not DataType.ARRAY. The table grid only offers
 * contains/doesNotContain for those columns (`getArrayFilterOperators`) and multi-select passes
 * `{ data: string[] }` — same shape as multi ENUM — so those types accept string or string-array.
 * ENUM and LOOKUP→inner types follow the same rule where the UI emits multi-value CONTAINS.
 */
function checkContainFilter(
  columnDataType: DataType,
  lookupType: DataType | null,
  data: unknown,
): boolean {
  const isStringArray = stringArrayOperandSchema.isValidSync(data)
  const isString = stringOperandSchema.isValidSync(data)

  switch (columnDataType) {
    case DataType.ARRAY:
      return isStringArray
    case DataType.STRING:
    case DataType.FILE_URL:
    case DataType.USER:
      return isString || isStringArray
    case DataType.ID:
      return isString
    case DataType.ENUM:
      return isString || isStringArray
    case DataType.LOOKUP: {
      if (lookupType === DataType.ARRAY) {
        return isStringArray
      }
      // Lookup to enum / string columns: multi-select uses string-array operand (same as ENUM case).
      if (
        lookupType === DataType.ENUM ||
        lookupType === DataType.STRING ||
        lookupType === DataType.FILE_URL ||
        lookupType === DataType.USER ||
        lookupType === DataType.ID
      ) {
        return isString || isStringArray
      }
      return isString
    }
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.NUMBER:
    case DataType.BOOLEAN:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return false
    case DataType.FORMULA_STRING:
      return isString
  }
}

function convertCalendarUnitToElasticCalendarUnit(calendarUnit: CalendarUnit): string {
  switch (calendarUnit) {
    case CalendarUnit.MINUTE:
      return 'm'
    case CalendarUnit.HOUR:
      return 'h'
    case CalendarUnit.DAY:
      return 'd'
    case CalendarUnit.WEEK:
      return 'w'
    case CalendarUnit.MONTH:
      return 'M'
    case CalendarUnit.QUARTER:
      return 'q'
    case CalendarUnit.YEAR:
      return 'y'
  }
}

/**
 * Converts a time interval and a calendar unit to an Elasticsearch
 * fixed_interval or calendar_interval string
 */
export function convertCalendarUnitToElasticInterval(
  interval: number,
  calendarUnit: CalendarUnit,
): string {
  const elasticCalendarUnit = convertCalendarUnitToElasticCalendarUnit(calendarUnit)
  return `${interval}${elasticCalendarUnit}`
}

/**
 * Converts a DateTimeOperandData object to an Elasticsearch date string
 * that is used in a range query.
 *
 * Flooring must be applied before offset because flooring could undo the offset.
 * This could take place if the flooring unit differs from the offset unit.
 * For example, for "this week" with a start of the week of Sunday,
 * the date needs to be floored by week first before applying the -1 day offset.
 */
export function convertDateTimeOperandDataToElastic(operand: DateTimeOperandData): string {
  let data = operand.data
  if (operand.floorUnit) {
    const elasticFloorUnit = convertCalendarUnitToElasticCalendarUnit(operand.floorUnit)
    data = `${data}/${elasticFloorUnit}`
  }
  if (operand.offset && operand.offsetUnit) {
    const elasticOffsetUnit = convertCalendarUnitToElasticCalendarUnit(operand.offsetUnit)
    if (operand.offset < 0) {
      data = `${data}${operand.offset}${elasticOffsetUnit}`
    } else {
      data = `${data}+${operand.offset}${elasticOffsetUnit}`
    }
  }
  return data
}

/**
 * Converts an operand of filter to a human readable string in the user's locale
 *
 * If it is a range filter, the operator is translated into dynamic range as a part of the human readable string
 */
function getHumanReadableFromOperandData(
  columnDataType: DataType,
  locale: LocaleCode = LocaleCode.EN_US,
  operand: OperandData,
  operator?: FilterOperator,
): string | undefined {
  // Special case: Range filters will construct the human readable operand with the operator
  if (checkRangeFilter(columnDataType, operand)) {
    if (validateStaticRangeFilter(columnDataType, operand)) {
      // If filter is a static range, get the human readable string for each bound recursively
      const lower = getHumanReadableFromOperandData(columnDataType, locale, operand.data.lower)
      const upper = getHumanReadableFromOperandData(columnDataType, locale, operand.data.upper)
      if (lower && upper) {
        if (operator === FilterOperator.RANGE) {
          return `Between ${lower} and ${upper}`
        } else if (operator === FilterOperator.NOT_IN_RANGE) {
          return `Not between ${lower} and ${upper}`
        }
      }
    } else if (validateDynamicDateRangeFilter(operand)) {
      // If filter is a dynamic range, get the human readable string for the dynamic value
      const dynamicRangeFilter = operand.data.dynamicValue
      if (dynamicRangeFilter.type === 'this') {
        return `This ${dynamicRangeFilter.unit}`
      } else if (dynamicRangeFilter.operator) {
        const label = `${titleCase(dynamicRangeFilter.operator)} ${dynamicRangeFilter.value} ${dynamicRangeFilter.unit}`
        return dynamicRangeFilter.excludeCurrent
          ? `${label}, ${describeExcludeCurrent(dynamicRangeFilter.excludeCurrent)}`
          : label
      }
    }
    throw new DossError('Cannot get human readable filter from invalid range filter', [])
  }
  // Format array
  if (Array.isArray(operand.data)) {
    return operand.data.join(', ')
  }
  // Format number
  if (numberOperandSchema.isValidSync(operand)) {
    return formatNumberByLocale(operand.data, locale)
  }
  // Format date and date time
  if (dateTimeOperandSchema.isValidSync(operand)) {
    if (operand.data === 'now') {
      return 'today'
    }
    if (columnDataType === DataType.DATE) {
      return formatDate(operand.data, { localeCode: locale })
    }
    return formatDateTimeByLocale(operand.data, locale)
  }
  return operand.data?.toString()
}

/**
 * Converts a table column filter object to a human readable filter displayed in the user's locale
 *
 * Used in display of the applied filter in chart configuration and chart (and soon, table) activity data.
 */
export function getHumanReadbleTableColumnFilterExpression(
  columnDataType: DataType,
  locale: LocaleCode = LocaleCode.EN_US,
  operand: OperandData,
  operator: FilterOperator,
): string {
  if (operator === FilterOperator.IS_DUPLICATE) {
    return 'Is duplicated'
  }

  const humanReadable = getHumanReadableFromOperandData(columnDataType, locale, operand, operator)
  if (humanReadable) {
    if (operator && operator !== FilterOperator.RANGE && operator !== FilterOperator.NOT_IN_RANGE) {
      // If the operator is not a range operator, prepend the operator to the human readable operand
      return `${titleCase(operator)} ${humanReadable} `
    }
    return humanReadable
  }
  return `${titleCase(operator)}`
}
